/**
 * Backend-authoritative cloud-demo onboarding state.
 *
 * The profile is polled only while provisioning is active. Retry and welcome
 * acknowledgement are server mutations followed by a fresh profile read, so
 * browser storage never becomes onboarding authority.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  acknowledgeDemoWelcome,
  fetchDemoSeedProfile,
  retryDemoSeed,
} from "./demoSeed/api";
import {
  EMPTY_PROFILE,
  parseProfile,
  POLL_INTERVAL_MS,
} from "./demoSeed/helpers";
import type {
  DemoSeedAction,
  DemoSeedProfile,
  UseDemoSeedStatusResult,
} from "./demoSeed/types";

export type {
  DemoDatasetSummary,
  DemoSeedPhase,
  DemoSeedProfile,
  DemoSeedProgress,
  DemoSeedStatus,
} from "./demoSeed/types";

export function useDemoSeedStatus(): UseDemoSeedStatusResult {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<DemoSeedProfile>(EMPTY_PROFILE);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState<DemoSeedAction | null>(null);
  const mountedRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPoll = useCallback((): void => {
    if (pollTimeoutRef.current !== null) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const loadProfile = useCallback(async (): Promise<void> => {
    clearPoll();
    try {
      const next = await fetchDemoSeedProfile();
      if (!mountedRef.current) return;
      setProfile(next ?? EMPTY_PROFILE);
      setRequestError(null);
      setLoading(false);
      if (next?.status === "seeding") {
        pollTimeoutRef.current = setTimeout(() => {
          void loadProfile();
        }, POLL_INTERVAL_MS);
      }
    } catch {
      if (!mountedRef.current) return;
      setLoading(false);
      setRequestError(
        "We couldn't load your demo workspace. Please try again.",
      );
    }
  }, [clearPoll]);

  useEffect(() => {
    mountedRef.current = true;
    void loadProfile();
    return (): void => {
      mountedRef.current = false;
      clearPoll();
    };
  }, [clearPoll, loadProfile]);

  const refresh = useCallback(async (): Promise<void> => {
    setActionPending("refresh");
    setActionError(null);
    await loadProfile();
    if (mountedRef.current) setActionPending(null);
  }, [loadProfile]);

  const retry = useCallback(async (): Promise<void> => {
    clearPoll();
    setActionPending("retry");
    setActionError(null);
    try {
      await retryDemoSeed();
      await loadProfile();
    } catch {
      if (mountedRef.current) {
        setActionError("We couldn't restart demo setup. Please try again.");
      }
    } finally {
      if (mountedRef.current) setActionPending(null);
    }
  }, [clearPoll, loadProfile]);

  const acknowledge = useCallback(async (): Promise<void> => {
    clearPoll();
    setActionPending("acknowledge");
    setActionError(null);
    try {
      await acknowledgeDemoWelcome();
      await loadProfile();
    } catch {
      if (mountedRef.current) {
        setActionError("We couldn't save that yet. Please try again.");
      }
    } finally {
      if (mountedRef.current) setActionPending(null);
    }
  }, [clearPoll, loadProfile]);

  return {
    loading,
    profile,
    requestError,
    actionError,
    actionPending,
    refresh,
    retry,
    acknowledge,
  };
}

export const __test__ = {
  POLL_INTERVAL_MS,
  parseProfile,
};
